export interface HelpArticle {
  id: string;
  title: string;
  summary: string;
  content: string;
}

export interface HelpCategory {
  key: string;
  label: string;
  articles: HelpArticle[];
}

export const helpCategories: HelpCategory[] = [
  /* ═══════════════════════════════════════════
     1. GETTING STARTED
     ═══════════════════════════════════════════ */
  {
    key: "getting-started",
    label: "Getting Started",
    articles: [
      {
        id: "overview",
        title: "What is AURA?",
        summary: "A quick look at what AURA does and where it gets its answers.",
        content: `## What is AURA?

AURA is a search engine for Diebold Nixdorf product docs — but instead of links, you get actual answers with sources.

It covers **VRP**, **VCO**, and **VE**. You ask a question, it pulls the answer from indexed documentation, and shows you exactly where it came from. No guessing, no hallucinations.

### You can use it to
- Look up product details and get cited answers
- Generate PRDs, epics, and user stories using slash commands
- Upload files (PDF, DOCX, images, etc.) and ask questions about them
- Share conversations with a link or export them as PDF/DOCX/XLSX

### A few things it won't do
- It won't answer questions about non-DN stuff
- It can't connect to live systems or pull real-time data
- If a doc isn't indexed, AURA will tell you it doesn't have enough info rather than making something up`,
      },
      {
        id: "quickstart",
        title: "Quick start guide",
        summary: "Get a great answer in three steps.",
        content: `## Quick start guide

### 1. Pick a response mode
Open settings and choose **Simple**, **Standard**, or **Enhanced**. You can't change this mid-conversation, so pick before you start chatting.

### 2. Ask something specific
The more detail you give, the better the answer. Include the product name, a version if you know it, and what you're trying to do.

**Good:** "How do I configure the MSR card reader in VRP v2024.1 for Germany?"
**Vague:** "How does card reader setup work?"

### 3. Check the sources and keep going
Every answer comes with source links so you can verify things yourself. You'll also see 3 follow-up suggestions — just click one to keep digging.

> If your question is too broad, AURA will ask you to narrow it down instead of giving a half-baked answer.`,
      },
    ],
  },

  /* ═══════════════════════════════════════════
     2. FEATURES
     ═══════════════════════════════════════════ */
  {
    key: "features",
    label: "Features",
    articles: [
      {
        id: "response-modes",
        title: "Response modes",
        summary: "Pick the right mode for the kind of answer you need.",
        content: `## Response modes

Pick a mode **before** you start chatting — you can't switch mid-conversation.

| Mode | When to use it |
|------|---------------|
| **Simple** | Quick lookups, L1 support — short and direct |
| **Standard** | Everyday use — detailed answers with full citations |
| **Enhanced** | Deep dives — separates facts from inference, extra cautious |

Most people stick with **Standard**. Use Enhanced when you're reviewing something carefully and need the extra precision.

To switch modes, just start a new chat.`,
      },
      {
        id: "agents",
        title: "Agents",
        summary: "Different agents for different jobs.",
        content: `## Agents

AURA has specialized agents — each one is tuned for a specific type of work.

**Available right now:**
- **Product Knowledge Base** — general product Q&A (everyone has this)
- **RFP Intelligence Platform** — RFP analysis and Fit/Gap work (needs admin access)

**How to switch:** Use the sidebar or open the Agent Store. Pin the ones you use often.

Each agent has its own conversation context, so switching agents means starting fresh — your previous chat doesn't carry over.

See a lock icon? That means you need admin access. Reach out to your admin to get it turned on.`,
      },
      {
        id: "slash-commands",
        title: "Slash commands",
        summary: "Pre-built templates for PRDs, epics, stories, and more.",
        content: `## Slash commands

Type \`/\` in the message box and you'll see a list of templates.

### Some useful ones

| Command | What you get |
|---------|-------------|
| \`/vrp prd\` | A product requirements document |
| \`/vrp epic\` | A detailed epic |
| \`/vrp stories\` | User stories with acceptance criteria |
| \`/vrp diagram\` | Mermaid diagram code |
| \`/ears event driven\` | "When X happens, the system shall do Y" |
| \`/vibe coding\` | R&D brainstorming mode |

**Try chaining them:** Start with \`/vrp initiative\`, then \`/vrp prd\`, then \`/vrp epic\`, then \`/vrp stories\` — and you've got a full feature spec.`,
      },
      {
        id: "attach-files",
        title: "File uploads",
        summary: "Drop files into a message and AURA will read them.",
        content: `## File uploads

You can attach files to any message — AURA will extract the text and use it as context.

**Supported:** PDF, DOCX, TXT, CSV, JSON, XML, PNG, JPEG, WEBP, SVG, LOG

**Limits:** 20 MB per file, up to 15 files per message.

For images, AURA runs OCR to read the text. Just make sure you're using a vision-capable model (GPT-4.1 or GPT-5 Mini) — GPT-5 Nano skips images entirely.`,
      },
      {
        id: "share-export",
        title: "Share & export",
        summary: "Send a link or download your conversation.",
        content: `## Share & export

**Sharing** — Hit the share button on any conversation to get a read-only link. The person you send it to doesn't need an AURA account.

**Exporting** — Download as PDF (formatted with citations), DOCX (editable), TXT (plain text), or XLSX (spreadsheet).

You can also rename, rate (thumbs up/down), or delete conversations from the sidebar.`,
      },
    ],
  },

  /* ═══════════════════════════════════════════
     3. TIPS & BEST PRACTICES
     ═══════════════════════════════════════════ */
  {
    key: "tips",
    label: "Tips & Best Practices",
    articles: [
      {
        id: "prompt-tips",
        title: "Writing good prompts",
        summary: "Better questions get better answers. Here's how.",
        content: `## Writing good prompts

The trick is simple: **say what product, ask something specific, and add context.**

### Examples

| Instead of... | Try... |
|--------------|--------|
| Tell me about VRP | How do I install VRP v2024.1 on a new POS terminal? |
| Checkout issues | What are known issues with VCO self-checkout in Italy? |
| Compare | Compare VRP card reader config for Germany vs France in a table |

### Quick tips
- Always mention the product — **VRP**, **VCO**, or **VE**
- Add a version if you have one (e.g., v2024.1)
- Mention the market — AURA knows country codes (IT, DE, FR, etc.)
- Ask for a format if you want one — "as a table", "step-by-step", "3 bullets"`,
      },
      {
        id: "conversations",
        title: "Managing conversations",
        summary: "When to keep going and when to start fresh.",
        content: `## Managing conversations

AURA remembers everything in the current chat. So you can say things like "What about Germany?" or "Put that in a table" and it'll know what you mean.

### When to start a new chat
- After about **20 messages** — answers tend to get less focused in long threads
- When you're **switching to a different topic** entirely
- When the **citations start looking generic**

Click **+ New Chat** in the sidebar. Your old chat is still saved.

Every answer comes with 3 follow-up suggestions — click them instead of retyping.`,
      },
    ],
  },

  /* ═══════════════════════════════════════════
     4. TROUBLESHOOTING
     ═══════════════════════════════════════════ */
  {
    key: "troubleshooting",
    label: "Troubleshooting",
    articles: [
      {
        id: "common-issues",
        title: "Common issues",
        summary: "Fixes for the most common problems people run into.",
        content: `## Common issues

| Problem | What to do |
|---------|-----------|
| **Answer feels vague** | Be more specific — mention the product, version, or market |
| **No citations showing up** | Your question is probably too broad. Try narrowing it down |
| **"I don't have enough information"** | The doc might not be indexed yet — syncs run daily at 2 AM UTC |
| **Image got ignored** | You need a vision model — switch to GPT-4.1 or GPT-5 Mini |
| **Can't see a feature** | Might be a role thing — check with your admin |
| **Answers going off-track** | Start a new chat. Long conversations lose focus |
| **Wrong product info** | Say "VCO" not just "checkout" — be explicit about the product |

Still stuck? Start a fresh conversation and rephrase with more detail.`,
      },
    ],
  },
];
